import { useSignalR } from '../components/providers/SignalRProvider.vue'
import { createSubscriptionAdder } from './subscription-utils'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  AccessRoleCreatedSignalRPayload,
  UserRoleAssignmentAddedSignalRPayload,
  UserRoleAssignmentRemovedSignalRPayload,
  AccessRoleDeletedSignalRPayload,
  PermissionsAddedToAccessRoleSignalRPayload,
  AccessRoleNameChangedSignalRPayload,
  PermissionsRemovedFromAccessRoleSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type AccessControlEventHandlers = {
  onAccessRoleCreated?: (
    payload: SignalRReceiveEvent<AccessRoleCreatedSignalRPayload>
  ) => void
  onAccessRoleDeleted?: (
    payload: SignalRReceiveEvent<AccessRoleDeletedSignalRPayload>,
    options?: SubscribeToEventOptions
  ) => void
  onUserRoleAssignmentAdded?: (
    payload: SignalRReceiveEvent<UserRoleAssignmentAddedSignalRPayload>
  ) => void
  onUserRoleAssignmentRemoved?: (
    payload: SignalRReceiveEvent<UserRoleAssignmentRemovedSignalRPayload>
  ) => void
  onPermissionsAddedToAccessRole?: (
    payload: SignalRReceiveEvent<PermissionsAddedToAccessRoleSignalRPayload>
  ) => void
  onAccessRoleNameChanged?: (
    payload: SignalRReceiveEvent<AccessRoleNameChangedSignalRPayload>
  ) => void
  onPermissionsRemovedFromAccessRole?: (
    payload: SignalRReceiveEvent<PermissionsRemovedFromAccessRoleSignalRPayload>
  ) => void
}

export type AccessControlEventOptions = {
  onAccessRoleCreated?: SubscribeToEventOptions
  onAccessRoleDeleted?: SubscribeToEventOptions
  onUserRoleAssignmentAdded?: SubscribeToEventOptions
  onUserRoleAssignmentRemoved?: SubscribeToEventOptions
  onPermissionsAddedToAccessRole?: SubscribeToEventOptions
  onAccessRoleNameChanged?: SubscribeToEventOptions
  onPermissionsRemovedFromAccessRole?: SubscribeToEventOptions
}

export function useAccessControlSignalREvents(
  handlers?: AccessControlEventHandlers,
  options?: AccessControlEventOptions
) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToAccessRoleCreated(
    cb: (payload: SignalRReceiveEvent<AccessRoleCreatedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AccessRoleCreatedSignalRPayload>(
      signalREvent.AccessControl.V1.AccessRoleCreated,
      cb,
      options
    )
  }

  function subscribeToUserRoleAssignmentAdded(
    cb: (
      payload: SignalRReceiveEvent<UserRoleAssignmentAddedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<UserRoleAssignmentAddedSignalRPayload>(
      signalREvent.AccessControl.V1.UserRoleAssigned,
      cb,
      options
    )
  }

  function subscribeToUserRoleAssignmentRemoved(
    cb: (
      payload: SignalRReceiveEvent<UserRoleAssignmentRemovedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<UserRoleAssignmentRemovedSignalRPayload>(
      signalREvent.AccessControl.V1.UserRoleUnassigned,
      cb,
      options
    )
  }

  function subscribeToAccessRoleDeleted(
    cb: (payload: SignalRReceiveEvent<AccessRoleDeletedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AccessRoleDeletedSignalRPayload>(
      signalREvent.AccessControl.V1.AccessRoleDeleted,
      cb,
      options
    )
  }

  function subscribeToPermissionsAddedToAccessRole(
    cb: (
      payload: SignalRReceiveEvent<PermissionsAddedToAccessRoleSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<PermissionsAddedToAccessRoleSignalRPayload>(
      signalREvent.AccessControl.V1.PermissionsAddedToAccessRole,
      cb,
      options
    )
  }

  function subscribeToAccessRoleNameChanged(
    cb: (
      payload: SignalRReceiveEvent<AccessRoleNameChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<AccessRoleNameChangedSignalRPayload>(
      signalREvent.AccessControl.V1.AccessRoleNameChanged,
      cb,
      options
    )
  }

  function subscribeToPermissionsRemovedFromAccessRole(
    cb: (
      payload: SignalRReceiveEvent<PermissionsRemovedFromAccessRoleSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<PermissionsRemovedFromAccessRoleSignalRPayload>(
      signalREvent.AccessControl.V1.PermissionsRemovedFromAccessRole,
      cb,
      options
    )
  }

  function subscribeToAccessControlEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []
      const addSubscription = createSubscriptionAdder(unsubscribeFunctions)

      addSubscription(
        handlers.onAccessRoleCreated,
        subscribeToAccessRoleCreated,
        options?.onAccessRoleCreated
      )
      addSubscription(
        handlers.onUserRoleAssignmentAdded,
        subscribeToUserRoleAssignmentAdded,
        options?.onUserRoleAssignmentAdded
      )
      addSubscription(
        handlers.onUserRoleAssignmentRemoved,
        subscribeToUserRoleAssignmentRemoved,
        options?.onUserRoleAssignmentRemoved
      )
      addSubscription(
        handlers.onAccessRoleDeleted,
        subscribeToAccessRoleDeleted,
        options?.onAccessRoleDeleted
      )
      addSubscription(
        handlers.onPermissionsAddedToAccessRole,
        subscribeToPermissionsAddedToAccessRole,
        options?.onPermissionsAddedToAccessRole
      )
      addSubscription(
        handlers.onAccessRoleNameChanged,
        subscribeToAccessRoleNameChanged,
        options?.onAccessRoleNameChanged
      )
      addSubscription(
        handlers.onPermissionsRemovedFromAccessRole,
        subscribeToPermissionsRemovedFromAccessRole,
        options?.onPermissionsRemovedFromAccessRole
      )

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromAccessControlEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToAccessControlEvents,
    unsubscribeFromAccessControlEvents
  }
}
